/**
 * Tenant Registry
 *
 * The client's answer to "which organizations does this user have?", taken from
 * the regional APIs rather than from the access token.
 *
 * `GET /users/me/tenants` is region-local — each regional API only sees its own
 * database — so the Auth0 tenants claim keeps its one irreplaceable job: telling
 * the SPA which regions to ask. What the claim no longer does is decide whether
 * an organization exists, what it is called, or what the user's role in it is.
 * Those come from the database, which is also the only thing the API consults
 * when it authorizes a request: a claim can never grant or deny access
 * (ESCLOUD-268).
 *
 * Regions are asked lazily. The regions the user is known to have something in
 * (claims plus what this browser remembers) are asked on load; every *deployed*
 * region is only swept when a caller has to prove an organization absent, so an
 * EU-only user does not get a user record created in every region.
 *
 * Consumers do not read the loading flags. They ask a question —
 * `useTenantLookup(id)` or `useTenantsOverview()` — and get an answer that
 * distinguishes "absent" from "we cannot tell yet". Those hooks also request the
 * sweep on the caller's behalf, so no answer can come back "absent" because a
 * caller forgot to ask for the proof.
 */

import { useAuth0 } from '@auth0/auth0-react';
import type { IUserTenantResponse } from '@escloud/types';
import { useQueries } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useApi } from './api-context';
import { useRegion } from './region-context';
import { useTenant } from './tenant-context';

import { userKeys } from '@/hooks/use-current-user';
import { useRegions } from '@/hooks/use-regions';
import { DEFAULT_REGION, getApiUrlForRegion } from '@/lib/region-config';
import { readHintedRegions, rememberTenantRegion } from '@/lib/tenant-region-hints';
import { mergeTenantRegistry, type TenantRegistryEntry } from '@/lib/tenant-registry';

/**
 * Answer to "does this organization exist for this user?".
 *
 * `absent` is only ever produced by a completed sweep of every deployed region
 * with nothing unreachable. Anything less is `pending` or `unprovable` — never
 * `absent` — because acting on an unproven absence is what discards a real
 * organization.
 */
export type TTenantLookup =
    | { status: 'unasked' }
    | { status: 'pending' }
    | { status: 'confirmed'; entry: TenantRegistryEntry }
    | { status: 'absent' }
    | { status: 'unprovable' };

/** Answer to "does this user have any organization at all?". */
export interface TenantsOverview {
    entries: TenantRegistryEntry[];
    status: 'pending' | 'some' | 'none' | 'unprovable';
}

interface TenantRegistryValue {
    /** Every organization we can currently account for, server answers winning. */
    entries: TenantRegistryEntry[];

    /** Every region we asked has answered or failed at least once. */
    isSettled: boolean;

    /** At least one region is being (re)fetched, so the picture may still change. */
    isFetching: boolean;

    /** A sweep of every deployed region has been requested and has settled. */
    isComplete: boolean;

    /**
     * Whether absence can be concluded at all. False when a region — or the
     * deployed-region list itself, or the token claims — did not answer: we
     * cannot then tell "not a member" from "could not ask".
     */
    canProveAbsence: boolean;

    /** Ask every deployed region, not only the ones the user is known to use. */
    requestFullSweep: () => void;
}

const TenantRegistryContext = createContext<TenantRegistryValue | undefined>(undefined);

export function TenantRegistryProvider({ children }: { children: ReactNode }) {
    const { isAuthenticated } = useAuth0();
    const { tenantClaims, claimsLoaded, claimsFailed } = useRegion();
    const api = useApi();
    const {
        data: deployedRegions,
        isPending: deployedRegionsPending,
        isError: deployedRegionsError,
    } = useRegions();

    const [sweepRequested, setSweepRequested] = useState(false);

    // Regions the user is known to have an organization in. The default region
    // is always included: it is the one the SPA talks to before anything else
    // is known, so asking it creates nothing that did not already exist.
    const knownRegions = useMemo(() => {
        const claimed = tenantClaims.map((claim) => String(claim.region ?? ''));
        const regions = [DEFAULT_REGION, ...readHintedRegions(), ...claimed];
        return [...new Set(regions.map((region) => region.trim().toLowerCase()))].filter(Boolean);
    }, [tenantClaims]);

    // Usable only when the list actually arrived AND is not empty. An empty list
    // would let a sweep "widen" to nothing and then report a completed proof it
    // never performed.
    const sweepRegions = useMemo(
        () => (deployedRegions?.items ?? []).map((region) => region.code.trim().toLowerCase()),
        [deployedRegions]
    );
    const deployedRegionsKnown = sweepRegions.length > 0;

    const regions = useMemo(
        () => (sweepRequested ? [...new Set([...knownRegions, ...sweepRegions])] : knownRegions),
        [knownRegions, sweepRegions, sweepRequested]
    );

    const results = useQueries({
        queries: regions.map((region) => ({
            queryKey: userKeys.tenantsInRegion(region),
            queryFn: () => api.users.getUserTenants(getApiUrlForRegion(region)),
            enabled: isAuthenticated && claimsLoaded,
            staleTime: 30_000,
        })),
    });

    const requestFullSweep = useCallback(() => setSweepRequested(true), []);

    const value = useMemo<TenantRegistryValue>(() => {
        const answeredRegions: string[] = [];
        const serverTenants: IUserTenantResponse[] = [];
        let hasUnavailableRegion = false;

        results.forEach((result, index) => {
            if (result.isSuccess) {
                // Only a CURRENT answer may contradict a claim. A region whose
                // answer is being replaced still holds the previous snapshot,
                // and letting that veto a freshly minted claim is what pulled
                // the user out of an organization created seconds earlier.
                if (!result.isFetching) {
                    answeredRegions.push(regions[index]);
                }
                serverTenants.push(...result.data.items);
            } else if (result.isError) {
                hasUnavailableRegion = true;
            }
        });

        const isFetching = results.some((result) => result.isFetching);
        const isSettled =
            (isAuthenticated ? results.every((result) => !result.isPending) : true) && claimsLoaded;

        const entries = mergeTenantRegistry(tenantClaims, serverTenants, answeredRegions);

        // Every way in which we could be blind. A missing deployed-region list
        // is one of them: without it a sweep proves nothing, however complete
        // the queries we did run may look.
        const canProveAbsence =
            !hasUnavailableRegion &&
            !claimsFailed &&
            !deployedRegionsError &&
            (deployedRegionsPending || deployedRegionsKnown);

        return {
            entries,
            isSettled,
            isFetching,
            isComplete:
                sweepRequested && isSettled && !isFetching && !deployedRegionsPending && deployedRegionsKnown,
            canProveAbsence,
            requestFullSweep,
        };
    }, [
        claimsFailed,
        claimsLoaded,
        deployedRegionsError,
        deployedRegionsKnown,
        deployedRegionsPending,
        isAuthenticated,
        regions,
        requestFullSweep,
        results,
        sweepRequested,
        tenantClaims,
    ]);

    // A server answer is the authoritative region for a tenant, so record it:
    // the next reload can address that tenant's own regional API even if the
    // token claim never catches up.
    useEffect(() => {
        value.entries
            .filter((entry) => entry.source === 'server')
            .forEach((entry) => rememberTenantRegion(entry.tenant.id, entry.tenant.region));
    }, [value.entries]);

    return <TenantRegistryContext.Provider value={value}>{children}</TenantRegistryContext.Provider>;
}

function useTenantRegistry(): TenantRegistryValue {
    const context = useContext(TenantRegistryContext);
    if (!context) {
        throw new Error('useTenantRegistry must be used within a TenantRegistryProvider');
    }
    return context;
}

/**
 * Ask about one organization and get the proof status with the answer. Requests
 * the full region sweep when the question cannot be answered without it, so no
 * caller has to remember to.
 */
export function useTenantLookup(tenantId: string | null | undefined): TTenantLookup {
    const { entries, isSettled, isComplete, canProveAbsence, requestFullSweep } = useTenantRegistry();

    const entry = tenantId ? entries.find((candidate) => candidate.tenant.id === tenantId) : undefined;
    const needsProof = !!tenantId && !entry;

    useEffect(() => {
        if (needsProof && isSettled && !isComplete) {
            requestFullSweep();
        }
    }, [isComplete, isSettled, needsProof, requestFullSweep]);

    if (!tenantId) return { status: 'unasked' };
    if (entry) return { status: 'confirmed', entry };
    if (!isSettled) return { status: 'pending' };
    if (!canProveAbsence) return { status: 'unprovable' };
    if (!isComplete) return { status: 'pending' };
    return { status: 'absent' };
}

/**
 * Ask whether the user has any organization at all. "None" is the statement that
 * sends someone into creating one, so it needs the same proof as an individual
 * absence — including on a first-ever sign-in, where there is no selected
 * organization to trigger it.
 */
export function useTenantsOverview(): TenantsOverview {
    const { entries, isSettled, isComplete, canProveAbsence, requestFullSweep } = useTenantRegistry();

    const needsProof = entries.length === 0;

    useEffect(() => {
        if (needsProof && isSettled && !isComplete) {
            requestFullSweep();
        }
    }, [isComplete, isSettled, needsProof, requestFullSweep]);

    if (!isSettled) return { entries, status: 'pending' };
    if (entries.length > 0) return { entries, status: 'some' };
    if (!canProveAbsence) return { entries, status: 'unprovable' };
    if (!isComplete) return { entries, status: 'pending' };
    return { entries, status: 'none' };
}

/**
 * The organizations to offer in a switcher: what the regional APIs answered,
 * merged with claims for regions that have not answered yet.
 */
export function useTenantList(): { entries: TenantRegistryEntry[]; isLoading: boolean } {
    const { entries, isSettled } = useTenantRegistry();
    return { entries, isLoading: !isSettled };
}

/**
 * The organization currently in context.
 *
 * `entry` is whatever the registry has, which may still be a token claim for a
 * region that has not answered — so its name and role can be stale.
 * `isConfirmed` says whether a regional API vouched for it: gate anything that
 * must not act on a stale role (admin affordances) on that, not on the entry
 * alone. Either way the API enforces the real role on every request.
 */
export function useCurrentTenant(): { entry: TenantRegistryEntry | null; isConfirmed: boolean } {
    const { tenantId } = useTenant();
    const lookup = useTenantLookup(tenantId);

    return useMemo(() => {
        const entry = lookup.status === 'confirmed' ? lookup.entry : null;
        return { entry, isConfirmed: entry?.source === 'server' };
    }, [lookup]);
}

/**
 * Persist the organization the user is in, once a regional API has vouched for
 * it. Deliberately not done by TenantProvider from the URL alone: a deep link to
 * something that does not exist must not become the remembered selection, and
 * clearing a selection must not be undone by the very route the user is being
 * redirected away from.
 */
export function useConfirmedTenantPersistence(): void {
    const { tenantId, storedTenantId, selectTenant } = useTenant();
    const lookup = useTenantLookup(tenantId);

    const confirmedId = lookup.status === 'confirmed' ? lookup.entry.tenant.id : null;

    useEffect(() => {
        if (confirmedId && confirmedId !== storedTenantId) {
            selectTenant(confirmedId);
        }
    }, [confirmedId, selectTenant, storedTenantId]);
}
